import { Plus } from 'lucide-react';
import React, { useState } from 'react';

import { EmptyState } from '@/components/nft/EmptyState';
import { NFTGridCard } from '@/components/nft/GridCard';
// Components
import { NFTFilters } from '@/components/nft/NftFilter';
import { NFTForm } from '@/components/nft/NftForm';
import { NFTListItem } from '@/components/nft/NftListItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// Types
import { NFT, NFTFormData } from '@/types/nft';

const NFTManagementPage = () => {
  const [nfts, setNfts] = useState<NFT[]>([
    {
      id: '1',
      name: 'Cosmic Dragon #1',
      description: 'A magnificent cosmic dragon soaring through the stars',
      image: 'https://picsum.photos/400/400?random=1',
      price: 1.5,
      currency: 'ETH',
      collection: 'Cosmic Dragons',
      attributes: [
        { trait_type: 'Background', value: 'Nebula' },
        { trait_type: 'Eyes', value: 'Cosmic' },
        { trait_type: 'Rarity', value: 'Legendary' },
      ],
      status: 'listed',
      createdAt: '2024-01-15',
      tokenId: '12345',
    },
    {
      id: '2',
      name: 'Digital Punk #42',
      description: 'Vintage digital punk with futuristic vibes',
      image: 'https://picsum.photos/400/400?random=2',
      price: 0.89,
      currency: 'ETH',
      collection: 'Digital Punks',
      attributes: [
        { trait_type: 'Background', value: 'Cyber' },
        { trait_type: 'Accessory', value: 'VR Headset' },
      ],
      status: 'minted',
      createdAt: '2024-01-14',
      tokenId: '12346',
    },
  ]);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedNft, setSelectedNft] = useState<NFT | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [collectionFilter, setCollectionFilter] = useState<string>('all');

  // Form state for create/edit
  const [formData, setFormData] = useState<NFTFormData>({
    name: '',
    description: '',
    image: '',
    price: '',
    currency: 'ETH',
    collection: '',
    attributes: [{ trait_type: '', value: '' }],
  });

  const filteredNfts = nfts.filter((nft) => {
    const matchesSearch =
      nft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nft.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || nft.status === statusFilter;
    const matchesCollection =
      collectionFilter === 'all' || nft.collection === collectionFilter;

    return matchesSearch && matchesStatus && matchesCollection;
  });

  const handleCreate = () => {
    const newNft: NFT = {
      id: Date.now().toString(),
      name: formData.name,
      description: formData.description,
      image:
        formData.image || 'https://picsum.photos/400/400?random=' + Date.now(),
      price: parseFloat(formData.price),
      currency: formData.currency,
      collection: formData.collection,
      attributes: formData.attributes.filter(
        (attr) => attr.trait_type && attr.value,
      ),
      status: 'draft',
      createdAt: new Date().toISOString().split('T')[0],
    };

    setNfts((prev) => [newNft, ...prev]);
    setIsCreateDialogOpen(false);
    resetForm();
  };

  const handleEdit = () => {
    if (!selectedNft) return;

    const updatedNft: NFT = {
      ...selectedNft,
      name: formData.name,
      description: formData.description,
      image: formData.image,
      price: parseFloat(formData.price),
      currency: formData.currency,
      collection: formData.collection,
      attributes: formData.attributes.filter(
        (attr) => attr.trait_type && attr.value,
      ),
    };

    setNfts((prev) =>
      prev.map((nft) => (nft.id === selectedNft.id ? updatedNft : nft)),
    );
    setIsEditDialogOpen(false);
    resetForm();
  };

  const handleDelete = () => {
    if (!selectedNft) return;
    setNfts((prev) => prev.filter((nft) => nft.id !== selectedNft.id));
    setIsDeleteDialogOpen(false);
  };

  const openEditDialog = (nft: NFT) => {
    setSelectedNft(nft);
    setFormData({
      name: nft.name,
      description: nft.description,
      image: nft.image,
      price: nft.price.toString(),
      currency: nft.currency,
      collection: nft.collection,
      attributes:
        nft.attributes.length > 0
          ? nft.attributes
          : [{ trait_type: '', value: '' }],
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (nft: NFT) => {
    setSelectedNft(nft);
    setIsDeleteDialogOpen(true);
  };

  const handleView = (nft: NFT) => {
    // Implement view functionality
    console.log('View NFT:', nft);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image: '',
      price: '',
      currency: 'ETH',
      collection: '',
      attributes: [{ trait_type: '', value: '' }],
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">NFT Management</h1>
          <p className="text-muted-foreground">
            Create, manage, and track your NFT collection
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create NFT
        </Button>
      </div>

      {/* Filters */}
      <NFTFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        collectionFilter={collectionFilter}
        onCollectionFilterChange={setCollectionFilter}
      />

      {/* NFT Views */}
      <Tabs defaultValue="grid" className="space-y-6">
        <TabsList>
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
        </TabsList>

        <TabsContent value="grid">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
            {filteredNfts.map((nft) => (
              <NFTGridCard
                key={nft.id}
                nft={nft}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
                onView={handleView}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="list">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4">NFT</th>
                    <th className="text-left p-4">Collection</th>
                    <th className="text-left p-4">Price</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Created</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNfts.map((nft) => (
                    <NFTListItem
                      key={nft.id}
                      nft={nft}
                      onEdit={openEditDialog}
                      onDelete={openDeleteDialog}
                      onView={handleView}
                    />
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Empty State */}
      {filteredNfts.length === 0 && (
        <EmptyState onCreate={() => setIsCreateDialogOpen(true)} />
      )}

      {/* Create Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Create New NFT</DialogTitle>
            <DialogDescription>
              Fill in the details to create a new NFT.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[70vh] pr-2">
            <NFTForm
              formData={formData}
              onChange={setFormData}
              onSubmit={handleCreate}
              onCancel={() => {
                setIsCreateDialogOpen(false);
                resetForm();
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditDialogOpen(false);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit NFT</DialogTitle>
            <DialogDescription>Update the NFT details below.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[70vh] pr-2">
            <NFTForm
              formData={formData}
              onChange={setFormData}
              onSubmit={handleEdit}
              onCancel={() => {
                setIsEditDialogOpen(false);
                resetForm();
              }}
              isEditing={true}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Delete NFT</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedNft?.name}&quot;?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="flex-1"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NFTManagementPage;
